import { IMetricFormParams, IMetricFormScope } from "./controllers/metric-form";
import { IEditDynamicMetricScope, IEditDynamicMetricParams } from "./controllers/edit-dynamic-metric";
import dayjs from "@gtmhub/dayjs";
import { UnitTypeLong } from "dayjs";
import { IOkrGridContributorAdditionalParams } from "@gtmhub/goals/components/okr-grid-contributors/okr-grid-contributors.component";
import { IMetric } from "./models/models";
import { CascadingLink, ILinksSpecDetail, Links } from "@webapp/links/models/links.models";

export type MetricDeadLine = "none" | "hardDeadline" | "hardAndSoftDeadline";

export const metricFormStrings = {
  metricCustomFields: "metricCustomFields",
  session: "session",
  allowedActions: "allowedActions",
  double: "double",
  boolean: "boolean",
  empty: "",
  day: "day" as UnitTypeLong,
  default: {
    date: "0001-01-01T00:00:00Z",
  },
  manageData: "ManageData",
  admin: "admin",
  planningSession: "planningSession",
};

export function setMetricDueDates(scope: IEditDynamicMetricScope | IMetricFormScope): void {
  // if no deadlines are set OR/AND metric is anti-target KR --> use Session's end
  if (
    scope.useDeadline === "none" ||
    ((scope.metric.manualType === metricFormStrings.double || scope.metric.manualType === metricFormStrings.empty) &&
      (scope.metric.targetOperator === "should_stay_above" || scope.metric.targetOperator === "should_stay_below"))
  ) {
    scope.metric.dueDate = metricFormStrings.default.date;
    scope.metric.softDueDate = metricFormStrings.default.date;

    return;
  }

  if (scope.useDeadline === "hardDeadline") {
    scope.metric.dueDate = dayjs(scope.dates.dueDate).endOf("day").toISOString();
    scope.metric.softDueDate = metricFormStrings.default.date;

    return;
  }

  if (scope.useDeadline === "hardAndSoftDeadline") {
    scope.metric.dueDate = dayjs(scope.dates.dueDate).endOf("day").toISOString();
    scope.metric.softDueDate = dayjs(scope.dates.softDueDate).endOf("day").toISOString();
  }
}

export function restoreMetricDueDates(scope: IEditDynamicMetricScope | IMetricFormScope): void {
  switch (scope.useDeadline) {
    case "none":
      scope.metric.dueDate = metricFormStrings.default.date;
      scope.metric.softDueDate = metricFormStrings.default.date;
      break;
    case "hardDeadline":
      if (scope.metric.dueDate == metricFormStrings.default.date) {
        scope.dates.dueDate = dayjs(scope.session.end).subtract(1, metricFormStrings.day).endOf("day").toISOString();
        scope.metric.softDueDate = metricFormStrings.default.date;
      } else {
        scope.dates.dueDate = scope.metric.dueDate;
      }
      break;
    case "hardAndSoftDeadline":
      if (scope.metric.dueDate === metricFormStrings.default.date) {
        scope.dates.dueDate = dayjs(scope.session.end).subtract(1, metricFormStrings.day).endOf("day").toISOString();
      } else {
        scope.dates.dueDate = scope.metric.dueDate;
      }
      if (scope.metric.softDueDate === metricFormStrings.default.date) {
        scope.dates.softDueDate = dayjs(scope.dates.dueDate).subtract(1, metricFormStrings.day).toISOString();
      } else {
        scope.dates.softDueDate = scope.metric.softDueDate;
      }
      break;
  }
}

export function disableDatesOutsideSessionTimeframe(scope: IEditDynamicMetricScope | IMetricFormScope, stateParams: IEditDynamicMetricParams | IMetricFormParams): void {
  if (scope.session.start == null || scope.session.end == null) {
    return;
  }

  const minDate = dayjs(scope.session.start).add(1, metricFormStrings.day);
  const maxDate = dayjs(scope.session.end).subtract(1, metricFormStrings.day);
  const maxSoftDueDate = dayjs(scope.session.end).subtract(2, metricFormStrings.day);

  const setDefaultDatepickerRanges = (): void => {
    scope.dueDatePicker = {
      minDate,
      maxDate,
    };

    scope.softDueDatePicker = {
      minDate,
      maxDate: maxSoftDueDate,
    };
  };

  const setDefaultDates = (): void => {
    scope.dates.dueDate = maxDate.toISOString();
    scope.dates.softDueDate = maxSoftDueDate.toISOString();
  };

  setDefaultDatepickerRanges();
  setScopeDatesRespectingMetric(scope, stateParams, () => setDefaultDates());

  // update the date and the max date for the soft deadline because it should earlier than the hard deadline
  scope.$watch(
    "dueDate",
    () => {
      const oneDayBeforeHardDueDate = dayjs(scope.dates.dueDate).subtract(1, metricFormStrings.day);
      const isDiffBetweenHardAndSoftDatesInvalid = dayjs(scope.dates.dueDate).diff(scope.dates.softDueDate) <= 0;

      if (scope.useDeadline === "hardDeadline") {
        scope.dates.softDueDate = oneDayBeforeHardDueDate.toISOString();
      }

      if (isDiffBetweenHardAndSoftDatesInvalid) {
        scope.dates.softDueDate = oneDayBeforeHardDueDate.toISOString();
      }

      scope.softDueDatePicker.maxDate = oneDayBeforeHardDueDate;
    },
    true
  );

  // if user is editing and chooses not to use deadlines --> set them both to the max dates to pass form validations
  scope.$watch(
    "useDeadline",
    () => {
      if (scope.useDeadline === "none") {
        setDefaultDates();
        setDefaultDatepickerRanges();
      }
    },
    true
  );
}

function setScopeDatesRespectingMetric(
  scope: IEditDynamicMetricScope | IMetricFormScope,
  stateParams: IEditDynamicMetricParams | IMetricFormParams,
  setDefaultDates: () => void
) {
  const setDefaultDeadlineState = (): void => {
    setDefaultDates();
    scope.useDeadline = "none";
  };

  if (!stateParams.metricId) {
    setDefaultDeadlineState();
    return;
  }

  const dueDateIsDefault = scope.metric.dueDate === metricFormStrings.default.date;
  const softDueDateIsDefault = scope.metric.softDueDate === metricFormStrings.default.date;

  if (softDueDateIsDefault && dueDateIsDefault) {
    setDefaultDeadlineState();
    return;
  }

  if (softDueDateIsDefault && !dueDateIsDefault) {
    scope.useDeadline = "hardDeadline";
    scope.dates.dueDate = scope.metric.dueDate;
    scope.dates.softDueDate = dayjs(scope.metric.dueDate).subtract(1, metricFormStrings.day).toISOString();
    return;
  }

  if (!softDueDateIsDefault && !dueDateIsDefault) {
    scope.useDeadline = "hardAndSoftDeadline";
    scope.dates.dueDate = scope.metric.dueDate;
    scope.dates.softDueDate = scope.metric.softDueDate;
  }
}

export function canCascadeMetric(metric: IMetric, isGoalAbandoned: boolean): boolean {
  if (!metric) {
    return false;
  }

  return !isGoalAbandoned && metric.targetOperator === "at_least";
}

export function calculateMetricDelta(metric?: IMetric): number {
  if (!metric) {
    return 0;
  }

  const target = metric.target || 0;
  const initialValue = metric.initialValue || 0;

  return target - initialValue;
}

export interface IContributorMetricValues {
  value: number;
  percentage: number;
}

export function calculateContributorMetricValues(cascadingMetric?: IMetric, contributorMetric?: IMetric): IContributorMetricValues {
  const contributorMetricValues: IContributorMetricValues = {
    value: 0,
    percentage: 0,
  };

  if (!cascadingMetric || !contributorMetric) {
    return contributorMetricValues;
  }

  switch (cascadingMetric.cascadeType) {
    case "delta_value":
      contributorMetricValues.value = calculateMetricDelta(contributorMetric);
      contributorMetricValues.percentage = (contributorMetricValues.value / calculateMetricDelta(cascadingMetric)) * 100;

      return contributorMetricValues;
    case "absolute_value":
      contributorMetricValues.value = contributorMetric.target;
      contributorMetricValues.percentage = (contributorMetricValues.value / calculateMetricDelta(cascadingMetric)) * 100;

      return contributorMetricValues;
    default:
      return contributorMetricValues;
  }
}

export function createContributorMetric(cascadingMetric?: IMetric, goalId?: string): IMetric {
  if (!cascadingMetric) {
    return {};
  }

  return {
    name: cascadingMetric.name || "",
    manualType: cascadingMetric.manualType || "",
    targetOperator: cascadingMetric.targetOperator || "at_least",
    format: cascadingMetric.format || { suffix: "", prefix: "", fractionSize: 0 },
    initialValue: 0,
    target: 0,
    ownerIds: cascadingMetric.ownerIds || [],
    goalId: goalId || "",
  };
}

export function isContributingMetric(metric: IMetric | IOkrGridContributorAdditionalParams): boolean {
  return !!metric.links?.spec?.contributing;
}

export function generateMetricLinksSpec(linkType: CascadingLink, targetMetricIds: string[]): Links<"metric"> {
  return {
    spec: {
      [linkType]: generateMetricLinksSpecDetails(targetMetricIds),
    },
  };
}

export function generateMetricLinksSpecDetails(metricIds: string[]): ILinksSpecDetail<"metric">[] {
  return metricIds.map<ILinksSpecDetail<"metric">>((metricId) => ({ type: "metric", id: metricId }));
}
